import { Avatar, Badge, Box, Divider, Grid, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/free-solid-svg-icons";
import VideocamIcon from "@mui/icons-material/Videocam";
import ChatIcon from "@mui/icons-material/Chat";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ArchiveIcon from "@mui/icons-material/Archive";
import ArticleIcon from "@mui/icons-material/Article";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PanToolIcon from "@mui/icons-material/PanTool";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import { useTranslation } from "react-i18next";
import { useAppointments } from "../../hooks/useAppointments";

function calculateAge(birthDate: string): string {
  if (!birthDate || birthDate === "Invalid date") return "-";
  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return "-";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate()))
    age--;
  return String(age);
}

function formatDate(dateStr: string): string {
  if (!dateStr || dateStr === "Invalid date") return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
}

interface ActionBtn {
  label: string;
  Icon: React.ElementType;
  color: string;
  action?: () => void;
}

const headerSx = { display: "flex", gap: 2, mb: 2.5, alignItems: "flex-start" };

const avatarSx = {
  width: 80,
  height: 80,
  border: "3px solid #00BFA5",
  flexShrink: 0
};

const headerInfoSx = { flex: 1, minWidth: 0 };

const nameSx = {
  fontWeight: 700,
  fontSize: "16px",
  textTransform: "uppercase",
  lineHeight: 1.2,
  paddingRight: "80px"
};

const infoLineSx = { fontSize: "14px", mt: 0.5, color: "#333" };

const linksRowSx = { display: "flex", gap: 3, mt: 1 };

const linkSx = {
  color: "#00897B",
  fontWeight: 600,
  fontSize: "14px",
  cursor: "pointer"
};

const badgeSx = {
  width: "100%",
  "& .MuiBadge-badge": {
    width: 12,
    height: 12,
    minWidth: 12,
    borderRadius: "50%",
    backgroundColor: "#0DBDA7",
    border: "2px solid #fff",
    top: 6,
    right: 6,
    padding: 0
  }
};

const actionBtnSx = (color: string, hasAction: boolean) => ({
  width: "100%",
  backgroundColor: color,
  borderRadius: "12px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  padding: "14px 8px",
  cursor: hasAction ? "pointer" : "not-allowed",
  height: "85px",
  userSelect: "none",
  transition: "opacity 0.15s",
  "&:active": { opacity: hasAction ? 0.82 : 1 }
});

const actionIconSx = { color: "#fff", fontSize: "28px" };

const actionLabelSx = {
  color: "#fff",
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1.2
};

interface Props {
  patient: any;
  onVideoCall?: () => void;
  onClinicalRecord?: () => void;
}

export default function PatientDetailPanel({
  patient,
  onVideoCall,
  onClinicalRecord
}: Props) {
  const { t } = useTranslation();
  const { upcoming } = useAppointments(
    patient.clinicalFileId ?? patient.fileId
  );
  const hasVideo = upcoming.length > 0;

  const gender =
    patient.gender === 1
      ? t("utentes.male")
      : patient.gender === 2
      ? t("utentes.female")
      : "-";

  const age = calculateAge(patient.birthDate);
  const dob = formatDate(patient.birthDate);

  const buttonGroups: ActionBtn[][] = [
    [
      {
        label: t("patientDetail.videocall"),
        Icon: VideocamIcon,
        color: "#1A56C4",
        action: onVideoCall
      },
      { label: t("patientDetail.messages"), Icon: ChatIcon, color: "#2E7D32" }
    ],
    [
      {
        label: t("patientDetail.clinicalRecord"),
        Icon: MedicalServicesIcon,
        color: "#F59E0B",
        action: onClinicalRecord
      },
      {
        label: t("patientDetail.archive"),
        Icon: ArchiveIcon,
        color: "#9E9E9E"
      },
      {
        label: t("patientDetail.documents"),
        Icon: ArticleIcon,
        color: "#9E9E9E"
      }
    ],
    [
      {
        label: t("patientDetail.diagnoses"),
        Icon: AccessibilityNewIcon,
        color: "#00ACC1"
      },
      {
        label: t("patientDetail.antecedents"),
        Icon: NoteAddIcon,
        color: "#1A237E"
      },
      {
        label: t("patientDetail.allergies"),
        Icon: PanToolIcon,
        color: "#E65100"
      },
      {
        label: t("patientDetail.medication"),
        Icon: LocalPharmacyIcon,
        color: "#00796B"
      }
    ],
    [
      {
        label: t("patientDetail.requestMCDT"),
        Icon: FavoriteBorderIcon,
        color: "#7B1FA2"
      },
      {
        label: t("patientDetail.historyMCDT"),
        Icon: AssignmentIcon,
        color: "#AB47BC"
      }
    ],
    [
      {
        label: t("patientDetail.prescriptions"),
        Icon: ReceiptLongIcon,
        color: "#00695C"
      },
      {
        label: t("patientDetail.prescriptionHistory"),
        Icon: HistoryIcon,
        color: "#78909C"
      }
    ]
  ];

  // Hide buttons whose feature isn't implemented yet (no `action`), and drop
  // any group left empty so its divider doesn't render.
  const visibleGroups = buttonGroups
    .map(group => group.filter(btn => btn.action))
    .filter(group => group.length > 0);

  return (
    <Box>
      {/* Patient info header */}
      <Box sx={headerSx}>
        <Avatar src={patient.pictureUrl} alt={patient.name} sx={avatarSx} />
        <Box sx={headerInfoSx}>
          <Typography sx={nameSx}>{patient.name}</Typography>
          <Typography sx={infoLineSx}>
            {gender} | {dob} | {age} {t("utentes.years")}
          </Typography>
          <Typography sx={infoLineSx}>
            {t("patientDetail.nhsId")} - {patient.nhsId ?? "-"}
          </Typography>
          <Box sx={linksRowSx}>
            <Typography sx={linkSx}>{t("patientDetail.rnu")}</Typography>
            <Typography sx={linkSx}>
              {t("patientDetail.pathologies")}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Action button groups — only buttons with a wired-up action are shown.
         As features get implemented, give the button an `action` and it appears. */}
      {visibleGroups.map((group, gi) => (
        <Box key={gi}>
          {gi > 0 && <Divider sx={{ my: 1.5 }} />}
          <Grid container spacing={1.5}>
            {group.map((btn, bi) => {
              const isVideo = btn.Icon === VideocamIcon;
              const showBadge = isVideo && hasVideo;
              return (
                <Grid item xs={6} key={bi}>
                  <Badge badgeContent={showBadge ? " " : 0} sx={badgeSx}>
                    <Box onClick={btn.action} sx={actionBtnSx(btn.color, true)}>
                      {isVideo ? (
                        <FontAwesomeIcon
                          icon={faVideo}
                          style={{ color: "#fff", fontSize: 22 }}
                        />
                      ) : (
                        <btn.Icon sx={actionIconSx} />
                      )}
                      <Typography sx={actionLabelSx}>{btn.label}</Typography>
                    </Box>
                  </Badge>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}
